import {
  Component,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { Store } from "@ngrx/store";
import {
  SetAccountingBasisData,
  SetAnalyticsData,
  SetExportToExcelData,
  SetExtrasData,
  SetPeriodsData,
  SetRevisionsData,
  SetSourcesData,
} from "src/app/store/actions/export-to-excel.actions";
import {
  AccountingBasisData,
  AnalyticsData,
  PeriodsData,
  SourcesData,
  RevisionsData,
  ExtrasData,
  ExportToExcel,
} from "src/app/store/models/export-to-excel.model";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { ExportToExcelService } from "./export-to-excel.service";
import { JsonData, Statements } from "./export-to-excel.model";
import { environment } from "src/environments/environment";
import { AppService } from "src/app/app.service";
import dayjs from "dayjs";
import { CommonModalService } from "../common-modal/common-modal.service";
import { GlobalHeaderDataService } from "src/app/global-header/global-header-data.service";
import { NzFormatEmitEvent, NzTreeNodeOptions } from "ng-zorro-antd/tree";
import {
  VAIconRegistryService,
  close,
  infoCircle,
  slidersHorizontal,
  bulletedList,
  exchange,
  calendar,
  calculator,
  redo,
  chartLine,
  plus,
  closeCircle,
} from "@va/core-library/icons";

@Component({
  selector: "app-export-to-excel",
  templateUrl: "./export-to-excel.component.html",
  styleUrls: ["./export-to-excel.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ExportToExcelComponent implements OnInit, OnChanges {
  faCurrentSettings = "sliders-horizontal";
  faStatements = "bulleted-list";
  faSources = "exchange";
  faTimePeriods = "calendar";
  faHistoricalEstimates = "redo";
  faAccounting = "calculator";
  faAnalytics = "chart-line";

  @Input() isModalOpen: boolean;
  @Input() pageParams: {
    pg: string;
    cut: string;
    ticker: string;
    source: string;
  };
  @Output() onClose: EventEmitter<boolean> = new EventEmitter();
  buildTime = "Low";
  panelsData;
  previousSettingsJson;
  buildTimeClass;
  buildTimeDisplay = "Low";
  tickerFullName = "";
  selectedTabIndex = 1;

  assets = environment.assetUrl;

  tabs = [
    { title: "Overview", header: true, icon: null, seperator: false },
    {
      title: "Current Settings",
      header: false,
      icon: this.faCurrentSettings,
      seperator: true,
    },
    { title: "Customize", header: true, icon: null, seperator: false },
    {
      title: "Statements",
      header: false,
      icon: this.faStatements,
      seperator: false,
    },
    { title: "Sources", header: false, icon: this.faSources, seperator: false },
    {
      title: "Time Periods",
      header: false,
      icon: this.faTimePeriods,
      seperator: false,
    },
    {
      title: "Historical Estimates",
      header: false,
      icon: this.faHistoricalEstimates,
      seperator: false,
    },
    {
      title: "Analytics (Labs)",
      header: false,
      icon: this.faAnalytics,
      seperator: false,
    },
  ];

  constructor(
    private store: Store<AppState>,
    private eteService: ExportToExcelService,
    private appService: AppService,
    private dialogService: CommonModalService,
    private headerDataService: GlobalHeaderDataService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([
      close,
      infoCircle,
      slidersHorizontal,
      bulletedList,
      exchange,
      calendar,
      calculator,
      closeCircle,
      redo,
      chartLine,
      plus,
    ]);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.pageParams &&
      changes.pageParams.currentValue !== changes.pageParams.previousValue
    ) {
      //page has changed so invalidate cached data
      this.panelsData = undefined;
      this.statementsData = undefined;
      this.previousSettingsJson = undefined;
    }

    //if modal is closed return
    if (!this.isModalOpen) {
      return;
    }

    //if pg cur and ticker are not available return
    const { pg, cut, ticker } = this.pageParams;
    if (!pg || !cut || !ticker) {
      return;
    }

    this.eteService.getEtePanelData(cut, ticker).subscribe((response) => {
      this.panelsData = response.panels;
      this.initializeSections();
    });

    this.eteService
      .getExportExcelPrevHistory(ticker, "json")
      .subscribe((response) => {
        if (response.status === "success") {
          this.previousSettingsJson = response.jsonData;
        }
      });
  }

  ngOnInit() {
    //subscribe to individual sections
    this.subscribeToSources();
    this.subscribeToAnalytics();
    this.subscribeToPeriods();
    this.subscribeToRevisions();
    this.subscribeToAccountingBasis();
    this.subscribeToExtras();
    this.subscribeToSubHeader();
  }

  /**
   *
   * @param name name of the section | eg. growthtypes
   * @returns corresponding section
   */
  getSectionDataFromName(name: string) {
    return this.panelsData.find(
      (section) => section.name.toLowerCase() === name.toLocaleLowerCase(),
    );
  }

  initializeSections(hidden?: boolean) {
    this.storeSourcesData(this.getSectionDataFromName("sources"), hidden);
    this.storePeriodsData(this.getSectionDataFromName("periods"));
    this.storeRevisionsData(this.getSectionDataFromName("revisions"));
    this.storeAnalyticsData(this.getSectionDataFromName("GrowthTypes"));
    this.storeAccountingData(this.getSectionDataFromName("accounting basis"));
    this.storeExtrasData();

    if (!hidden) {
      this.makeStatementTree();
      this.calculateComplexityMeter();
    }
  }

  closeModal() {
    this.onClose.emit(true);
    this.selectedTabIndex = 1;
  }

  onSectionTabChanged() {
    this.calculateComplexityMeter();
  }

  onLastUsedSettingsClicked() {
    if (this.previousSettingsJson) {
      this.applyLastUsedSettings(this.previousSettingsJson);
      this.makeStatementTree();
      return;
    }

    this.eteService
      .getExportExcelPrevHistory(this.pageParams.ticker, "json")
      .subscribe((response) => {
        if (response.status === "success") {
          this.previousSettingsJson = response.jsonData;
          this.applyLastUsedSettings(this.previousSettingsJson);
        }
      });
  }

  applyLastUsedSettings(jsonData: JsonData) {
    const exportToExcelData: ExportToExcel = {
      accountingBasis: jsonData["Accounting basis"],
      adjustments: jsonData.Adjustments,
      analytics: jsonData.GrowthTypes,
      extras: jsonData.Extras,
      periods: jsonData.Periods,
      revisions: jsonData.Revisions,
      sources: jsonData.Sources,
    };

    this.store.dispatch(new SetExportToExcelData(exportToExcelData));
    this.calculateComplexityMeter();
  }

  onResetClicked() {
    this.initializeSections();
  }

  exportCurrentView() {
    const { cut, ticker } = this.pageParams;
    //if we already have panel data just initialize and return
    this.noOfReportedYears = 3;
    this.noOfForecastYears = 10;
    // To set the currency for default export
    if (
      window.cut === "STD" &&
      window.MEData.selectedCurrency &&
      window.MEData.selectedCurrency !== "Base"
    ) {
      this.selectedCurrency = window.MEData.selectedCurrency;
    } else {
      this.selectedCurrency = "";
    }
    if (this.panelsData) {
      this.initializeSections(true);
      this.setDefault();
      this.performExport(true);
      this.resetPeriodRange();
      return;
    }

    this.eteService.getEtePanelData(cut, ticker).subscribe((response) => {
      this.panelsData = response.panels;
      this.initializeSections(true);
      this.setDefault();
      this.performExport(true);
      this.resetPeriodRange();
    });
  }

  private resetPeriodRange() {
    this.noOfReportedYears = 2;
    this.noOfForecastYears = 3;
  }

  private setDefault() {
    this.periodsDataCopy.annuals = true;
    this.periodsDataCopy.interims = true;
    this.sourcesDataCopy.AllSourceData.SelectedSources = [];
    this.sourcesDataCopy.AllSourceData.filings = false;
    if (
      this.sourcesDataCopy.AllSourceData.ConsensusType &&
      this.sourcesDataCopy.AllSourceData.ConsensusType.length > 0
    ) {
      this.sourcesDataCopy.AllSourceData.ConsensusType = [
        this.sourcesDataCopy.AllSourceData.ConsensusType[0],
      ];
    }
    this.sourcesDataCopy.type = "column";
    this.revisionsDataCopy.revision_date_list = [];
    this.accountingDataCopy.selectedBasis = "All";
    this.analyticsDataCopy.default = "";
    this.analyticsDataCopy.values = [];
    this.revisionsDataCopy.values = [];
  }

  onExportClick() {
    if (this.calculateComplexityMeter() > 120) {
      const confirmExportDialog = this.dialogService.create({
        body: "Your request may take longer than expected, try reducing the number of sources/statements for the export and try again.",
        title: null,
        accentColor: "#36abad",
        actionPrimaryText: "Go back to Export Options",
        actionSecondaryText: "Export anyway",
        position: { top: 30 },
        onPrimaryClick: () => {
          confirmExportDialog.close();
        },
        onSecondaryClick: () => {
          confirmExportDialog.close();
          this.performExport();
        },
      });
      return;
    }

    const statements = this.sourcesDataCopy.statements;
    if (
      statements.NMV.length === 0 &&
      statements.SCH.length === 0 &&
      statements.STD.length === 0 &&
      Object.keys(statements.SAR).length === 0
    ) {
      this.dialogService.create({
        body: "To export to Excel, please select at least one statement",
        title: null,
        position: { top: 30 },
      });
      return;
    }

    const allSourceData = this.sourcesDataCopy.AllSourceData;
    if (
      allSourceData.SelectedSources.length === 0 &&
      allSourceData.ConsensusType.length === 0 &&
      allSourceData.va_actuals === false
    ) {
      this.dialogService.create({
        body: "To export to Excel, please select at least one broker or calculated source.",
        title: null,
        position: { top: 30 },
      });
      return;
    }

    this.performExport();
  }

  performExport(isDefaultView = false) {
    let jsonData: JsonData = {
      "Accounting basis": this.accountingDataCopy,
      Adjustments: { missingDataSelected: "implied" },
      Extras: this.extrasDataCopy,
      GrowthTypes: this.analyticsDataCopy,
      Periods: this.periodsDataCopy,
      Revisions: this.revisionsDataCopy,
      Sources: this.sourcesDataCopy,
    };

    const includeCustom = [];
    const excludedCustom = [];

    this.setBrokerList();
    this.brokersList.forEach((bro) => {
      if (bro.selected_cc) {
        includeCustom.push(bro.sourceid);
      } else {
        excludedCustom.push(bro.sourceid);
      }
    });

    //prepend '0D' and today's date in revisions mandatorily
    jsonData = {
      ...jsonData,
      Sources: {
        ...jsonData.Sources,
        AllSourceData: {
          ...jsonData.Sources.AllSourceData,
          IncludeCustomConsensus: includeCustom,
          ExcludeCustomConsensus: excludedCustom,
        },
      },
      Revisions: {
        ...jsonData.Revisions,
        revision_date_list: [
          this.getFormattedDate(new Date()),
          ...jsonData.Revisions.revision_date_list.filter(
            (rev) => rev !== null,
          ),
        ],
        values: [
          "0D",
          ...jsonData.Revisions.values.filter((val) => val !== null),
        ],
      },
    };

    //Decode Sources
    if (jsonData["Sources"]["statements"]) {
      for (const cut in jsonData["Sources"]["statements"]) {
        if (Array.isArray(jsonData["Sources"]["statements"][cut])) {
          const newSheetList = jsonData["Sources"]["statements"][cut].map(
            (sl) => decodeURIComponent(sl),
          );
          jsonData["Sources"]["statements"][cut] = newSheetList;
        } else if (cut === "SAR") {
          for (const b of Object.keys(jsonData["Sources"]["statements"][cut])) {
            const newSheetList = jsonData["Sources"]["statements"][cut][b].map(
              (sl) => decodeURIComponent(sl),
            );
            jsonData["Sources"]["statements"][cut][b] = newSheetList;
          }
        }
      }
    }

    this.eteService
      .saveExportExcelHistory(jsonData, this.pageParams.ticker)
      .subscribe((response: any) => {
        if (response.status === "success") {
          const trackerid = response.trackerid;
          this.previousSettingsJson = jsonData;
          this.eteService
            .prepareExport(
              this.pageParams.ticker,
              trackerid,
              isDefaultView ? this.selectedCurrency : "",
            )
            .subscribe((response) => {
              if (response.status === "success") {
                //show export available for download notification
              }
            });
          this.createSuccessNotification();
          this.logAction(trackerid);
          //empty cached data so that when next ete pop up opens
          //new call is fired to fetch the following data (panlesData & statementsTreeData)
          this.panelsData = undefined;
          this.statementsData = undefined;
        }
      });

    this.closeModal();
  }

  logAction(trackerid: string) {
    let source = "";
    if (this.pageParams.source) {
      source = this.pageParams.source;
    } else {
      const sourceObj = this.getSectionDataFromName(
        "sources",
      ).values[1].values.find((computedSource) => computedSource.focus);
      source = sourceObj ? sourceObj.name : "";
    }

    this.appService.logUserAction(
      null,
      this.pageParams.ticker,
      this.pageParams.cut,
      "EXPORT_TO_EXCEL",
      { trackerId: trackerid, pg: this.pageParams.pg },
      source,
      null,
    );
  }

  subscribeToSubHeader() {
    this.store
      .select(stateGetter("globalSubheader", "subheaderTitle"))
      .subscribe((title: string) => {
        this.tickerFullName = title;
      });
  }

  createSuccessNotification() {
    const notiParams = {
      type: "success",
      title: "Export To Excel",
      msg: `${
        this.tickerFullName || this.pageParams.ticker
      }. This download may take a few minutes, thank you for your patience!`,
    };
    this.headerDataService.showNotification(notiParams);
  }

  createExportNotAllowedNotification() {
    const notiParams = {
      type: "error",
      title: "Export To Excel",
      msg: `Your previous request for ${this.pageParams.ticker} is still in progress, please wait for it to be completed.`,
    };
    this.headerDataService.showNotification(notiParams);
  }

  calculateComplexityMeter(): number {
    const complexityColors = {
      default: "build-time-default", //grey
      low: "build-time-low", //green
      medium: "build-time-medium", //orange
      high: "build-time-high", //red
    };

    //calculate periods
    const from = this.periodsDataCopy.selected_from;
    const to = this.periodsDataCopy.selected_to;
    const current = this.periodsDataCopy.currentquarter;

    //case when from, to or current are not yet initialzed and are ''
    if (!from || !to || !current) {
      return -1;
    }

    const nFrom = Number(from.match(/\d{4}/)[0]);
    const nTo = Number(to.match(/\d{4}/)[0]);
    const nCurrent = Number(current.match(/\d{4}/)[0]);
    const nInterim = Number(current.match(/\d{1}/)[0]);

    const yrsBeforeCurr = Math.abs(nCurrent - nFrom) * 5;
    const yrsAfterCurr = Math.abs(nTo - nCurrent);
    const noOfPeriods = 5 * yrsBeforeCurr + yrsAfterCurr + nInterim;

    //calculate NMV columns
    const nSelSources =
      this.sourcesDataCopy.AllSourceData.SelectedSources.length;
    const noOfRevs = this.revisionsDataCopy.values.length;
    const noNmvStatements = this.sourcesDataCopy.statements.NMV.length;
    const noOfNmvCols =
      (noNmvStatements || 1) * noOfRevs * noOfPeriods * nSelSources;

    //calculate SAR columns
    let noSarStatements = 1;
    for (const brokerStatements in this.sourcesDataCopy.statements.SAR) {
      noSarStatements += brokerStatements.length;
    }
    const noOfSarCols = noSarStatements * noOfRevs * noOfPeriods;

    const totalCols = noOfNmvCols + noOfSarCols;
    const timePerCol = 100;
    const buildTime = (totalCols * timePerCol) / 1000;

    this.buildTimeClass = complexityColors.default;
    if (buildTime < 120) {
      this.buildTimeClass = complexityColors.low;
      this.buildTimeDisplay = "Low";
    } else if (buildTime < 600) {
      this.buildTimeClass = complexityColors.medium;
      this.buildTimeDisplay = "Medium";
    } else if (buildTime >= 600) {
      this.buildTimeClass = complexityColors.high;
      this.buildTimeDisplay = "High";
    }

    return buildTime;
  }

  /*
   ========================================================================================================
   all Statements related variables and functions start here
   ========================================================================================================
   */
  allStatements: NzTreeNodeOptions[];
  allBrokersExcluedAndNon: NzTreeNodeOptions[];
  statementsData: Statements;

  makeStatementTree() {
    if (this.statementsData) {
      this.buildTree(this.statementsData);
      return;
    }

    this.eteService
      .getEteTree(this.pageParams.ticker)
      .subscribe((statements) => {
        this.statementsData = statements;
        this.buildTree(statements);
      });
  }

  buildTree(statements) {
    //default statement has to be pre selected while rendering tree

    /*
      default selections
      this stores keys that will be checked or halfChecked
      this is done so that we can expand only noode where node is checked
      if we do not expand while rendering tree, we don't get intermediate states checked
      this is a problem with the libraty. hence this workaround
      */
    const dselections = this.buildStatementsFromStore();

    //recursive function to fill default selections
    const buildCheckdNodes = (node: Statements) => {
      if (!node.children) {
        return;
      }

      node.children.forEach((childNode) => {
        buildCheckdNodes(childNode);
      });

      node.children.forEach((childNode) => {
        if (dselections.includes(childNode.item.id)) {
          dselections.push(node.item.id);
        }
      });
    };

    buildCheckdNodes(statements[0]);
    buildCheckdNodes(statements[1]);
    buildCheckdNodes(statements[2]);

    this.allStatements = [
      this.convertToTreeNodeFormat(statements[0], dselections),
    ];

    this.allBrokersExcluedAndNon = [
      this.convertToTreeNodeFormat(statements[1], dselections),
      this.convertToTreeNodeFormat(statements[2], dselections),
    ];
  }

  /**
   *
   * @param type whether to return default satement as a string[] or an object
   * this function creates default statement that is to be checked when the tree is loaded
   * eg. chid_NMV_Geo
   */
  getDefaultStatement(type?: "as_array" | "as_object") {
    const { pg, cut, source } = this.pageParams;

    let defaultStatement = [];
    const defaultStatmentObj = { NMV: [], STD: [], SCH: [], SAR: {} };

    const defaultStatmentParts = ["child", cut, pg];
    if (source) {
      //insert source if source is present
      defaultStatmentParts.splice(2, 0, source);
      defaultStatement = [defaultStatmentParts.join("_")];
      defaultStatmentObj.SAR[source] = [pg];
    } else {
      defaultStatement = [defaultStatmentParts.join("_")];
      defaultStatmentObj[cut] = [pg];
    }

    if (type === "as_array") {
      return defaultStatement;
    }

    if (type === "as_object") {
      return defaultStatmentObj;
    }

    return defaultStatement;
  }

  buildStatementsFromStore() {
    const statementsObj = this.sourcesData
      ? { ...this.sourcesData.statements }
      : { NMV: [], STD: [], SCH: [], SAR: {} };
    let defaultStatement = [];

    defaultStatement = [
      ...statementsObj.NMV.map((st) => `child_NMV_${st}`),
      ...statementsObj.SCH.map((st) => `child_SCH_${st}`),
      ...statementsObj.STD.map((st) => `child_STD_${st}`),
    ];

    const sarSources = Object.keys(statementsObj.SAR);
    sarSources.forEach((sourceKey) => {
      const currSourceStatements = statementsObj.SAR[sourceKey];
      const statementIds = currSourceStatements.map(
        (st) => `child_SAR_${sourceKey}_${st}`,
      );
      defaultStatement = defaultStatement.concat(statementIds);
    });

    return defaultStatement;
  }

  /**
   *
   * @param statements input statement tree to convert
   * @param defaultChecked list of keys for which corresponding node will be checked or half checked
   *
   * @returns statements (Type Statements) input tree converted to NzTreeNodeOption Type
   */
  convertToTreeNodeFormat(
    statements: Statements,
    defaultChecked: string[],
  ): NzTreeNodeOptions {
    const children = statements.children || [];
    const key = statements.item.id;
    const isLeaf =
      statements.children && statements.children.length ? false : true;
    return {
      title: statements.item.label,
      key: key,
      selectable: false,
      checked: isLeaf && defaultChecked.includes(key),
      expanded: defaultChecked.includes(key),
      isLeaf: isLeaf,
      children: children.map((child: Statements) =>
        this.convertToTreeNodeFormat(child, defaultChecked),
      ),
    };
  }

  /**
   *
   * @param checkedNode node from which all descendant leaves are calculated
   */
  getLeafNodeKeys(checkedNode: NzTreeNodeOptions) {
    if (checkedNode.isLeaf) {
      return [checkedNode.key];
    }

    let leafNodeKeys = [];
    checkedNode.children.forEach((node) => {
      leafNodeKeys = leafNodeKeys.concat(this.getLeafNodeKeys(node));
    });
    return leafNodeKeys;
  }

  getStatementParts(statement: string) {
    const parts = statement.split("_");
    if (parts.includes("SAR")) {
      return { cut: parts[1], source: parts[2], pg: parts[3] };
    }
    return { cut: parts[1], pg: parts[2] };
  }

  nzCheck(checkedNode: NzFormatEmitEvent, changedSARTree: boolean) {
    let childStatements = [];
    checkedNode.nodes.forEach((childNode: NzTreeNodeOptions) => {
      const currentChildStatements = this.getLeafNodeKeys(childNode);
      childStatements = childStatements.concat(currentChildStatements);
    });

    let statements: SourcesData["statements"] = {
      NMV: [],
      SCH: [],
      STD: [],
      SAR: {},
    };
    if (changedSARTree) {
      statements = { ...this.sourcesDataCopy.statements, SAR: {} };
    } else {
      statements = {
        ...this.sourcesDataCopy.statements,
        NMV: [],
        SCH: [],
        STD: [],
      };
    }

    childStatements.forEach((statement: string) => {
      const { cut, source, pg } = this.getStatementParts(statement);
      if (cut === "SAR") {
        if (statements.SAR[source]) {
          statements.SAR[source].push(pg);
        } else {
          statements.SAR[source] = [pg];
        }
      } else {
        statements[cut].push(pg);
      }
    });

    this.sourcesDataCopy.statements = statements;
  }

  /*
   ========================================================================================================
   all Statements related variables and functions end here
   ========================================================================================================
   */

  /*
   ========================================================================================================
   all Sources related variables and functions start here
   ========================================================================================================
   */
  sourcesData: SourcesData;
  sourcesDataCopy: SourcesData;

  allowedComputedSources = [];
  dropped_broker = [];
  restricted_broker = [];
  lastFilingsDate = "";
  disableCustomConsensus = false;

  sortKeys: { [key: string]: string | null } = {
    modeldate: null,
    name: null,
  };
  brokersList = [];

  subscribeToSources() {
    this.store
      .select("exportToExcel", "sources")
      .subscribe((sourcesData: SourcesData) => {
        this.sourcesData = JSON.parse(JSON.stringify(sourcesData));
        this.sourcesDataCopy = JSON.parse(JSON.stringify(sourcesData));
        this.onSourcePlacementChange(this.sourcesDataCopy.type);
      });
  }

  getItemFromName(name: string, data: []): any {
    return data.find(
      (item: any) => item.name.toLowerCase() === name.toLowerCase(),
    );
  }

  storeSourcesData(sourcesSection, hidden) {
    this.setBrokerList();
    this.restricted_broker = sourcesSection.restricted_broker;
    this.dropped_broker = sourcesSection.dropped_broker;

    const computedObjs = this.getItemFromName(
      "computed",
      sourcesSection.values,
    ).values;
    this.allowedComputedSources = computedObjs.filter(
      (obj: any) => obj.entitled,
    );
    this.allowedComputedSources = this.allowedComputedSources.map(
      (cs) => cs.name,
    );

    let consensusType = computedObjs.map((obj: any) => {
      if (obj.selected) {
        return obj.name;
      }

      if (!obj.selected && obj.name === "Custom") {
        this.disableCustomConsensus = true;
      }
      return null;
    });
    consensusType = consensusType.filter((ctype) => ctype !== null);

    let brokersObjs = this.getItemFromName(
      "brokers",
      sourcesSection.values,
    ).values;

    const reverse = sourcesSection.sort_order === "desc" ? true : false;
    const keyOnObject =
      sourcesSection.sort_column === "name" ? "name" : "modeldatetime";
    brokersObjs = this.appService.basicSort(
      brokersObjs,
      [keyOnObject],
      reverse,
    );

    let selectedSources = brokersObjs.map((obj: any) =>
      obj.selected ? obj.sourceid : null,
    );
    selectedSources = selectedSources.filter((sid) => sid !== null);

    const historicalObjs = this.getItemFromName(
      "historicals",
      sourcesSection.values,
    ).values;
    const filings = this.getItemFromName("filings", historicalObjs);
    this.lastFilingsDate = filings.modeldate;
    const isFilingsSelected = filings.selected;
    const vaActuals = this.getItemFromName("va actuals", historicalObjs);
    const isVaActualsSelected = vaActuals.selected;
    const historicalConsensus = this.getItemFromName(
      "historical consensus",
      historicalObjs,
    );
    const isHistoricalConsensusSelected = Boolean(
      historicalConsensus && historicalConsensus.selected,
    );

    //short for selected_cc_not_entitled
    const sccne = sourcesSection.selected_cc_not_entitled;
    //short for selected_excl_cc_not_entitled
    const seccne = sourcesSection.selected_excl_cc_not_entitled;

    const brokerState = {
      "0": { id: "exB", name: "All Excluded Broker", values: [] },
      "1": { id: "nexB", name: "All Non Excluded Broker", values: [] },
    };
    brokersObjs.forEach((broker: any) => {
      const isExcluded = broker.excluded ? "0" : "1";
      brokerState[isExcluded].values.push(broker.name);
    });

    const sourcesData: SourcesData = {
      AllSourceData: {
        ConsensusType: consensusType,
        Show_Only_Current: sourcesSection.show_only_current,
        SelectedSources: hidden ? selectedSources : [],
        filings: isFilingsSelected,
        va_actuals: isVaActualsSelected,
        historical_consensus: isHistoricalConsensusSelected,
        selected_cc_not_entitled: Boolean(sccne),
        selected_excl_cc_not_entitled: Boolean(seccne),
        non_entitled: sccne === undefined ? [] : sourcesSection.non_entitled,
        excl_non_entitled:
          seccne === undefined ? [] : sourcesSection.excl_non_entitled,
        IncludeCustomConsensus: [],
        ExcludeCustomConsensus: [],
      },
      type: sourcesSection.type,
      sort_order: sourcesSection.sort_order,
      sort_column: sourcesSection.sort_column,
      logic_based_consensus: Boolean(sourcesSection.date_custom_consensus),
      refdate: sourcesSection.date_custom_consensus || null,
      brokerState: brokerState,
      CustomizedSources: {},
      empty_column: true,
      statements: this.getDefaultStatement(
        "as_object",
      ) as SourcesData["statements"],
    };

    this.store.dispatch(new SetSourcesData(sourcesData));
  }

  setBrokerList() {
    this.brokersList = this.getSectionDataFromName("sources").values[3].values;
  }

  onSortChange({ key, value }: { key: string; value: string }) {
    //bring broker list to initial state
    this.brokersList = [
      ...this.getSectionDataFromName("sources").values[3].values,
    ];

    //allow sort only one column at a time
    this.sortKeys = { modeldate: null, name: null };
    this.sortKeys[key] = value;

    if (value === null) {
      //no sorting order applied -> then return orginal sort state
      //brokerList already in original sort state
      return;
    }

    const reverse = value === "descend" ? true : false;
    const keyOnObject = key === "name" ? "name" : "modeldatetime";
    this.brokersList = [
      ...this.appService.basicSort(this.brokersList, [keyOnObject], reverse),
    ];
  }

  onShowCurrentSwitch(switchedOn: boolean) {
    this.sourcesDataCopy.AllSourceData.Show_Only_Current = switchedOn;
    this.brokersList = [
      ...this.getSectionDataFromName("sources").values[3].values,
    ];
    if (switchedOn) {
      //first remove any excluded broker from selected sources
      //because it may be possible that an exluded broker was selected before
      const excludedBrokers = this.brokersList.filter((broker) => {
        return broker.name.toLowerCase() !== "filings" && broker.excluded;
      });

      excludedBrokers.forEach((broker) => {
        //perform uncheck operation (remove from selected list)
        this.onRowChecked(broker.sourceid, false);
      });

      //now filter brokerlist to discard excluded brokers
      this.brokersList = this.brokersList.filter((broker) => {
        return broker.name.toLowerCase() !== "filings" && !broker.excluded;
      });
    }
  }

  onRowChecked(sourceid: number, isChecked: boolean) {
    let selectedSources = this.sourcesDataCopy.AllSourceData.SelectedSources;
    if (selectedSources.includes(sourceid) && !isChecked) {
      //remove this source
      selectedSources = selectedSources.filter((source) => source !== sourceid);
    }

    if (!selectedSources.includes(sourceid) && isChecked) {
      //add source id to slected sources
      selectedSources.push(sourceid);
      //sorting is necessary to avoid unnecessary diffs when comparing
      selectedSources = this.appService.basicSort(selectedSources);
    }

    this.sourcesDataCopy.AllSourceData.SelectedSources = selectedSources;
  }

  onCheckAll(isChecked: boolean) {
    let selectedSources = this.sourcesDataCopy.AllSourceData.SelectedSources;
    if (isChecked) {
      //check all the brokers that are currently visible
      this.brokersList.forEach((broker) => {
        if (!selectedSources.includes(broker.sourceid) && isChecked) {
          //add source id to slected sources
          selectedSources.push(broker.sourceid);
          //sorting is necessary to avoid unnecessary diffs when comparing
        }
      });
      selectedSources = this.appService.basicSort(selectedSources);
    } else {
      //uncheck all that are currently visible on screen
      this.brokersList.forEach((broker) => {
        if (selectedSources.includes(broker.sourceid) && !isChecked) {
          selectedSources = selectedSources.filter(
            (source) => source !== broker.sourceid,
          );
        }
      });
    }

    this.sourcesDataCopy.AllSourceData.SelectedSources = selectedSources;
  }

  onChangeHistoricals(name: string, isChecked: boolean) {
    this.sourcesDataCopy.AllSourceData[name] = isChecked;
  }

  toggleComputedSources(source: string, isChecked: boolean) {
    let sourceTypes = this.sourcesDataCopy.AllSourceData.ConsensusType;
    if (sourceTypes.includes(source) && !isChecked) {
      //remove this source
      sourceTypes = sourceTypes.filter((sname) => sname !== source);
    }

    if (!sourceTypes.includes(source) && isChecked) {
      //add this source
      sourceTypes.push(source);
      //sorting is necessary to avoid unnecessary diffs when comparing
      sourceTypes = this.appService.basicSort(sourceTypes);
    }
    this.sourcesDataCopy.AllSourceData.ConsensusType = sourceTypes;
  }

  onSourcePlacementChange(place: string) {
    if (place === "tab") {
      this.extrasDataCopy.splitIntoTabs = true;
    }
  }

  /*
   ========================================================================================================
   all Sources related variables and functions end here
   ========================================================================================================
   */

  /*
   ========================================================================================================
   all Periods related variables and functions start here
   ========================================================================================================
   */
  periodsData: PeriodsData;
  periodsDataCopy: PeriodsData;
  noOfYearsReported = [];
  noOfYearsForecast = [];
  noOfReportedYears = 0;
  noOfForecastYears = 0;
  selectedCurrency: string;

  subscribeToPeriods() {
    this.store
      .select(stateGetter("exportToExcel", "periods"))
      .subscribe((periodsData: PeriodsData) => {
        this.periodsData = JSON.parse(JSON.stringify(periodsData));
        this.periodsDataCopy = JSON.parse(JSON.stringify(periodsData));

        //case when selected_from, selected_to are not initialized and are ''
        if (!periodsData.selected_from || !periodsData.selected_to) {
          return;
        }

        const from = this.periodsData.selected_from.match(/\d{4}/)[0];
        const to = this.periodsData.selected_to.match(/\d{4}/)[0];
        const current = this.periodsData.firstEstimatedYr;

        this.noOfForecastYears = Number(to) - Number(current);
        this.noOfReportedYears = Number(current) - Number(from);
      });
  }

  storePeriodsData(periodsSection) {
    const periodsData: PeriodsData = {
      AandQOrder: "IAIA",
      annuals: periodsSection.annuals,
      interims: periodsSection.interims,
      currentquarter: periodsSection.currentquarter,
      firstEstimatedYr: Number(
        periodsSection.first_estimated_year.match(/\d{4}/)[0],
      ),
      firstEstimateYr: new Date().getFullYear() + 1,
      selected_from: this.buildSelectedRange().from,
      selected_to: this.buildSelectedRange().to,
    };

    const firstEstYear = periodsSection.first_estimated_year.match(/\d{4}/)[0];
    const firstEstYearIdx = periodsSection.values.findIndex((year) =>
      year.includes(firstEstYear),
    );

    this.noOfYearsReported.length = 0;
    this.noOfYearsForecast.length = 0;
    for (let i = 1; i <= firstEstYearIdx; i++) {
      this.noOfYearsReported.push(i);
    }
    for (let i = 1; i < periodsSection.values.length - firstEstYearIdx; i++) {
      this.noOfYearsForecast.push(i);
    }

    this.store.dispatch(new SetPeriodsData(periodsData));
  }

  buildSelectedRange() {
    const periodsSection = this.getSectionDataFromName("periods");
    const periods = periodsSection.values;
    const firstEstYear = periodsSection.first_estimated_year.match(/\d{4}/)[0];
    const firstEstYearIdx = periods.findIndex((year) =>
      year.includes(firstEstYear),
    );
    const fromIdx =
      this.noOfReportedYears === 0
        ? 0
        : Math.max(0, firstEstYearIdx - this.noOfReportedYears);
    const toIdx =
      this.noOfForecastYears === 0
        ? periods.length - 1
        : Math.min(
            periods.length - 1,
            firstEstYearIdx + this.noOfForecastYears,
          );
    return { from: periods[fromIdx], to: periods[toIdx] };
  }

  changeAiInclusion(earningType: string) {
    this.periodsDataCopy.annuals = false;
    this.periodsDataCopy.interims = false;
    if (earningType === "A") {
      this.periodsDataCopy.annuals = true;
    } else if (earningType === "I") {
      this.periodsDataCopy.interims = true;
    } else if (earningType === "AI") {
      this.periodsDataCopy.annuals = true;
      this.periodsDataCopy.interims = true;
    }
  }

  onPeriodRangeChanged({ forecast, reported }) {
    this.noOfForecastYears = Number(forecast) || this.noOfForecastYears;
    this.noOfReportedYears = Number(reported) || this.noOfReportedYears;

    const { from, to } = this.buildSelectedRange();
    this.periodsDataCopy.selected_from = from;
    this.periodsDataCopy.selected_to = to;
  }

  /*
   ========================================================================================================
   all Periods related variables and functions end here
   ========================================================================================================
   */

  /*
  ========================================================================================================
  all Historical Estimates related variables and functions start here
  ========================================================================================================
  */
  revisionsData: RevisionsData;
  revisionsDataCopy: RevisionsData;

  datePickerModel: Date[] = [];
  errorArray: string[] = [];
  preqChecked: boolean;

  subscribeToRevisions() {
    this.store
      .select("exportToExcel", "revisions")
      .subscribe((revisionsData: RevisionsData) => {
        this.revisionsData = JSON.parse(JSON.stringify(revisionsData));
        this.revisionsDataCopy = JSON.parse(JSON.stringify(revisionsData));

        this.datePickerModel = [];
        this.errorArray = [];

        //push null to datepickerModel for empty datepicker.
        //otherwise push JS date objects
        const revDateList = this.revisionsDataCopy.revision_date_list;
        const revValueList = this.revisionsDataCopy.values;

        const zeroDidx = revValueList.indexOf("0D");
        if (zeroDidx > -1) {
          revDateList.splice(zeroDidx, 1);
          revValueList.splice(zeroDidx, 1);
        }

        if (revDateList[0] === null) {
          this.datePickerModel.push(null);
          this.errorArray = [""];
        }

        if (revDateList.length >= 1) {
          revDateList.forEach((date: string, idx) => {
            if (date === "PreQ") {
              this.preqChecked = true;
            } else if (date !== null) {
              const dateObj = this.getDateObjectFrom(date);
              this.datePickerModel.push(dateObj);
              this.errorArray.push("");
              revValueList[idx] =
                this.appService.getFormattedDateDifference(dateObj);
            }
          });
        }

        if (this.datePickerModel.length === 0) {
          this.datePickerModel.unshift(null);
          revDateList.unshift(null);
          revValueList.unshift(null);
          this.errorArray.unshift("");
        }
      });
  }

  storeRevisionsData(revisionsSection) {
    this.datePickerModel = [];
    this.errorArray = [];
    this.preqChecked = false;

    const dates = revisionsSection.revision_date_list;
    const values = revisionsSection.values;
    const sortedDatesAndVals = this.zipSortAndUnzip(dates, values);

    const revisionsData: RevisionsData = {
      changed_date: revisionsSection.changed_date,
      type: revisionsSection.type === "off" ? "row" : revisionsSection.type,
      revision_date_list: sortedDatesAndVals.revisionDateListSorted,
      values: sortedDatesAndVals.revisionValuesSorted,
      effectiveStart: revisionsSection.effective_start,
    };

    if (revisionsSection.revision_date_list.length === 0) {
      revisionsData.revision_date_list = [null];
      revisionsData.values = [null];
    }

    if (
      revisionsSection.revision_date_list.length === 1 &&
      revisionsSection.revision_date_list[0] === "PreQ"
    ) {
      revisionsData.revision_date_list = [null, "PreQ"];
      revisionsData.values = [null, "PreQ"];
    }

    this.store.dispatch(new SetRevisionsData(revisionsData));
  }

  /**
   *
   * @param current Date object
   * returns true if date should be disabled
   */
  disabledDate = (current: Date): boolean => {
    const today = dayjs();
    const selected = dayjs(current);
    const effectiveStart =
      this.getSectionDataFromName("revisions").effective_start;
    const start =
      effectiveStart !== "" ? dayjs(effectiveStart, "YYYY-MM-DD") : dayjs(0);

    //dayjs objects support comparison like this
    return !(selected < today && selected >= start);
  };

  onDateChange = (dateObj: Date, index: number) => {
    this.revisionsDataCopy.revision_date_list[index] =
      this.getFormattedDate(dateObj);

    const validation = this.validateInputDate(dateObj);
    this.errorArray[index] = validation.msg;

    this.revisionsDataCopy.values[index] = null;
    // this.handleDisplayType();

    //if date is invalid
    //remove the difference string (eg -6M 20D) from revisionsDataCopy and return
    if (!validation.valid) {
      this.revisionsDataCopy.revision_date_list[index] = null;
      return;
    }

    //if date is valid
    //put difference string in revisionsDataCopy.values
    const dateDiff = this.appService.getFormattedDateDifference(dateObj);
    this.revisionsDataCopy.values[index] = dateDiff;
  };

  onPreqChange = (checked: boolean) => {
    this.preqChecked = checked;
    if (checked) {
      this.revisionsDataCopy.revision_date_list.push("PreQ");
      this.revisionsDataCopy.values.push("PreQ");
    } else {
      this.revisionsDataCopy.revision_date_list.pop();
      this.revisionsDataCopy.values.pop();
    }

    // this.handleDisplayType();
    // this.handleActionBtnsState();
  };

  onEffectiveStartClick = (idx) => {
    const effectiveStart = dayjs(
      this.revisionsDataCopy.effectiveStart,
      "YYYY-MM-DD",
    ).toDate();
    this.datePickerModel[idx] = effectiveStart;
    this.onDateChange(effectiveStart, idx);
  };

  addRevision = () => {
    const datePickerModelLen = this.datePickerModel.length;

    //null value displays empty datepicker
    this.datePickerModel.push(null);
    this.errorArray.push("");

    //revisionDateList and values might contain 'PreQ' at end
    //so insert new null value at index = datePickerModelLen
    //this maintains 'PreQ' at end of the array if present
    this.revisionsDataCopy.revision_date_list.splice(
      datePickerModelLen,
      0,
      null,
    );
    this.revisionsDataCopy.values.splice(datePickerModelLen, 0, null);
  };

  removeRevision = (index: number) => {
    this.datePickerModel.splice(index, 1);
    this.errorArray.splice(index, 1);
    this.revisionsDataCopy.revision_date_list.splice(index, 1);
    this.revisionsDataCopy.values.splice(index, 1);
    // this.handleActionBtnsState();
  };

  //force angular to track elemnts in datePickerModel by index
  trackByIdx(index: number): any {
    return index;
  }

  /**
   *
   * @param dateObj Javascript Date object
   * @returns formattedDate string as MM-DD-YYYY
   */
  getFormattedDate = (dateObj: Date) => {
    if (dateObj === null) {
      return null;
    }

    const formattedDate = dayjs(dateObj).format("MM-DD-YYYY");
    return formattedDate;
  };

  /**
   *
   * @param date date as a string in format MM-DD-YY
   * @returns Javascript date object
   */
  getDateObjectFrom = (date: string) => {
    const dateObj = dayjs(date, "MM-DD-YYYY").toDate();
    return dateObj;
  };

  validateInputDate = (date: Date) => {
    if (date === null && this.datePickerModel.length > 1) {
      return { valid: false, msg: "Format date as yyyy-mm-dd" };
    }

    if (this.getFormattedDate(date) === this.getFormattedDate(new Date())) {
      return { valid: false, msg: "Only previous dates can be defined" };
    }

    const count = this.revisionsDataCopy.revision_date_list.filter(
      (rvdate) => rvdate === this.getFormattedDate(date),
    ).length;

    const currIdx = this.datePickerModel.indexOf(date);
    const firstIdx = this.revisionsDataCopy.revision_date_list.indexOf(
      this.getFormattedDate(date),
    );
    if (count > 1 && currIdx > firstIdx) {
      return {
        valid: false,
        msg: "You have already selected this date. Each revision must be unique",
      };
    }

    return { valid: true, msg: "" };
  };

  zipSortAndUnzip(revisionDateList, revisionValues) {
    //zip together
    let revDatesAndVals = [];
    revisionDateList.forEach((date, i) => {
      revDatesAndVals.push({ date: date, value: revisionValues[i] });
    });

    //sort
    revDatesAndVals = this.appService.basicSort(
      revDatesAndVals,
      ["date"],
      true,
      this.timeMillisComparator,
    );

    //unzip
    const revisionDateListSorted = revDatesAndVals.map(({ date }) => date);
    const revisionValuesSorted = revDatesAndVals.map(({ value }) => value);

    return { revisionDateListSorted, revisionValuesSorted };
  }

  timeMillisComparator(dateStr: string) {
    if (dateStr.toLocaleLowerCase() === "preq") {
      return -1; //smallest time so that preq comes at last
    }
    const dateObj = dayjs(dateStr, "MM-DD-YYYY").toDate();
    return dateObj.getTime();
  }

  /*
  ========================================================================================================
  all Historical Estimates related variables and functions end here
  ========================================================================================================
  */

  /*
  =========================================================================================================
  all Analytics(Labs) related variables and functions start here
  =========================================================================================================
   */
  analyticsData: AnalyticsData;
  analyticsDataCopy: AnalyticsData;
  analyticsYearlist = [];

  subscribeToAnalytics() {
    this.store
      .select(stateGetter("exportToExcel", "analytics"))
      .subscribe((analyticsData: AnalyticsData) => {
        this.analyticsData = JSON.parse(JSON.stringify(analyticsData));
        this.analyticsDataCopy = JSON.parse(JSON.stringify(analyticsData));
      });
  }

  storeAnalyticsData(analyticsSection) {
    const years = this.getSectionDataFromName("periods").values;
    const startFrom = this.getSectionDataFromName("periods").from;
    const nStartFrom = String(startFrom.match(/\d{4}/)[0]);
    const trendYear = years.find((year) => year.includes(nStartFrom));

    const analyticsData: AnalyticsData = {
      allowedtypes: analyticsSection.allowedtypes,
      default: analyticsSection.default,
      name: "GrowthTypes",
      trendYear: trendYear,
      type: analyticsSection.type,
      values: analyticsSection.values,
    };

    this.generateYearList();

    this.store.dispatch(new SetAnalyticsData(analyticsData));
  }

  generateYearList = () => {
    const years = this.getSectionDataFromName("periods").values;
    this.analyticsYearlist = [];
    years.forEach((yearItem) => {
      this.analyticsYearlist.push({
        label: yearItem.match(/\d{4}/)[0],
        year: yearItem,
      });
    });
  };

  changeAnalyticInclusion(analyticName: string, isChecked: boolean) {
    if (isChecked && !this.analyticsDataCopy.values.includes(analyticName)) {
      this.analyticsDataCopy.values.push(analyticName);
    } else {
      const selectedAnalytics = this.analyticsDataCopy.values;
      this.analyticsDataCopy.values = selectedAnalytics.filter(
        (name) => name !== analyticName,
      );
    }
  }

  /*
  ========================================================================================================
  all Analytics(Labs) related variables and functions end here
  ========================================================================================================
  */

  /*
  ========================================================================================================
  all Accounting related variables and functions start here
  ========================================================================================================
  */
  accountingData: AccountingBasisData;
  accountingDataCopy: AccountingBasisData;

  subscribeToAccountingBasis() {
    this.store
      .select("exportToExcel", "accountingBasis")
      .subscribe((accountingBasisData: AccountingBasisData) => {
        this.accountingData = { ...accountingBasisData };
        this.accountingDataCopy = { ...accountingBasisData };
      });
  }

  storeAccountingData(accountingSection) {
    const accountingData: AccountingBasisData = {
      selectedBasis: accountingSection.values.find((basis) => basis.selected)
        .name,
    };

    this.store.dispatch(new SetAccountingBasisData(accountingData));
  }
  /*
  ========================================================================================================
  all Accounting related variables and functions end here
  ========================================================================================================
  */

  /*
  ========================================================================================================
  all Extra related variables and functions start here
  ========================================================================================================
  */
  extrasData: ExtrasData;
  extrasDataCopy: ExtrasData;

  subscribeToExtras() {
    this.store
      .select("exportToExcel", "extras")
      .subscribe((extras: ExtrasData) => {
        this.extrasData = JSON.parse(JSON.stringify(extras));
        this.extrasDataCopy = JSON.parse(JSON.stringify(extras));
      });
  }

  storeExtrasData() {
    const extrasData: ExtrasData = {
      exportLegend: true,
      sendByMail: false,
      splitIntoTabs: false,
    };

    this.store.dispatch(new SetExtrasData(extrasData));
  }
  /*
  ========================================================================================================
  all Extra related variables and functions end here
  ========================================================================================================
  */
}
